import type { ComboboxItem } from '@mantine/core';
import { TaskStatus } from '../../../../../api/tasks-client/task-client.type';
import type { FilterOption } from '../../../../../components/filters/filter-panel.type';
import {
  graphAmethyst,
  graphCambridgeBlue,
  graphLightRed,
  graphPictonBlue,
  graphSandyBrown,
  graphTropicalIndigo,
  skyDark,
} from '../../../../../styles/design-tokens';
import { StatusLabel } from '../../../initiative-list/filters/status-label';
import { Phase } from '../../flow-by-phase-tab/flow-by-phase-tab.type';

/**
 * Returns an array of filter options for task statuses
 * Each option contains a value from TaskStatus enum and a styled label component
 *
 * @returns {FilterOption[]} Array of filter options with status values and styled labels
 */
const getStatusOptions = (): FilterOption[] => {
  return [
    {
      value: TaskStatus.Blocked,
      label: <StatusLabel color={skyDark} label="Blocked" />,
    },
    {
      value: TaskStatus.Backlog,
      label: <StatusLabel color={graphTropicalIndigo} label="Ready" />,
    },
    {
      value: TaskStatus.WIP,
      label: <StatusLabel color={graphLightRed} label="WIP" />,
    },
    {
      value: TaskStatus.Review,
      label: <StatusLabel color={graphPictonBlue} label="In Review" />,
    },
    {
      value: TaskStatus.Test,
      label: <StatusLabel color={graphSandyBrown} label="In Test" />,
    },
    {
      value: TaskStatus.Deployable,
      label: <StatusLabel color={graphAmethyst} label="To Deploy" />,
    },
    {
      value: TaskStatus.Done,
      label: <StatusLabel color={graphCambridgeBlue} label="Done" />,
    },
  ];
};

/**
 * Returns an array of all available task statuses by mapping over the status options
 * and extracting just the status values
 *
 * @returns {TaskStatus[]} Array of all task status values
 */
const getDefaultTaskStatuses = (): TaskStatus[] => getStatusOptions().map((option) => option.value as TaskStatus);

/**
 * Returns an array of all available phase options
 *
 * @returns {ComboboxItem[]} Array of all phase options
 */
const getPhaseOptions = (): ComboboxItem[] => {
  return [
    {
      value: Phase.Blocked,
      label: 'Blocked',
    },
    {
      value: Phase.Backlog,
      label: 'Backlog',
    },
    {
      value: Phase.Wip,
      label: 'WIP',
    },
    {
      value: Phase.Review,
      label: 'In Review',
    },
    {
      value: Phase.Test,
      label: 'In Test',
    },
    {
      value: Phase.Deployable,
      label: 'To Deploy',
    },
    {
      value: Phase.Done,
      label: 'Done',
    },
  ];
};

export { getDefaultTaskStatuses, getPhaseOptions, getStatusOptions };
